import { useVenue } from "providers/venue"
import { useCallback, useMemo } from "react"
import _, { get } from "lodash"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useGeoLocation } from "providers/geolocation"
import { Q_VENUE } from "constant"

export const useMap = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    defaultVenue,
    isMultiVenueProject,
    levels,
    amenities,
    getCategoriesFromAmenity,
  } = useVenue()
  const { locationVenue: userLocationVenue } = useGeoLocation()
  const navigate = useNavigate()
  const { category } = useParams()

  const comingFromOrdinal = useMemo(
    () => searchParams.get("from-ordinal"),
    [searchParams]
  )

  const locationVenueId = userLocationVenue?.id || defaultVenue?.id

  const viewingVenueId = useMemo(
    () => searchParams.get(Q_VENUE) || locationVenueId,
    [searchParams, locationVenueId]
  )

  const sortedLevels = useMemo(
    () => _.orderBy(levels, "properties.ordinal", "desc"),
    [levels]
  )

  const levelInVenue = useMemo(
    () =>
      // Temporary condition should later be changed to properties.venue.id when prepareFeature has an ability to bindVenue
      isMultiVenueProject
        ? sortedLevels.filter(
            (level) => get(level, "properties.venue_id") === viewingVenueId
          )
        : sortedLevels,
    [isMultiVenueProject, sortedLevels, viewingVenueId]
  )

  const amenitiesInVenue = useMemo(
    () =>
      amenities.filter(
        (amenity) => amenity?.properties?.venue?.id === viewingVenueId
      ),
    [amenities, viewingVenueId]
  )

  const amenityCategoriesInVenue = useMemo(() => {
    return getCategoriesFromAmenity(amenitiesInVenue)
  }, [amenitiesInVenue, getCategoriesFromAmenity])

  const amenitiesInCategory = useMemo(
    () =>
      amenitiesInVenue.filter(
        (amenity) => amenity.properties.category === category
      ),
    [amenitiesInVenue, category]
  )

  const findNearestOrdinal = useCallback(
    (category, viewingOrdinal) => {
      const parsedOrdinal = !_.isNil(viewingOrdinal)
        ? Number(viewingOrdinal)
        : 0
      let nearestOrdinal = parsedOrdinal
      const nearestAmenity = _.minBy(amenitiesInCategory, (amenity) =>
        Math.abs(amenity.properties.ordinal - parsedOrdinal)
      )
      if (nearestAmenity) {
        nearestOrdinal = nearestAmenity.properties.ordinal
      }

      return nearestOrdinal
    },
    [amenitiesInCategory]
  )

  const viewingOrdinal = useMemo(
    () => searchParams.get("ordinal"),
    [searchParams]
  )

  const onChangeVenue = useCallback(
    (venueId) => {
      const currentParams = Object.fromEntries(searchParams.entries())
      const newParams = new URLSearchParams({
        ...currentParams,
        venue: venueId,
      }).toString()
      navigate(`/maps/ordinal/0${venueId !== null ? `?${newParams}` : ""}`, {
        replace: true,
      })
    },
    [navigate, searchParams]
  )
  const toggleAmenityFilter = useCallback(
    (amenityCategory) => {
      if (amenityCategory === category) {
        return navigate(
          `/maps/ordinal/${viewingOrdinal}?venue=${viewingVenueId}`
        )
      }
      const nearestOrdinal = findNearestOrdinal(amenityCategory, viewingOrdinal)
      navigate(
        `/maps/amenities/${amenityCategory}?ordinal=${nearestOrdinal}&venue=${viewingVenueId}`
      )
    },
    [navigate, findNearestOrdinal, viewingOrdinal, category, viewingVenueId]
  )
  return {
    locationVenueId,
    viewingVenueId,
    sortedLevels,
    levelInVenue,
    amenitiesInVenue,
    amenityCategoriesInVenue,
    amenitiesInCategory,
    viewingOrdinal,
    searchParams,
    category,
    comingFromOrdinal,
    // Function
    findNearestOrdinal,
    onChangeVenue,
    setSearchParams,
    toggleAmenityFilter,
  }
}
